import React from "react";
import { ProgressBar } from "@progress/kendo-react-progressbars";
import { StatusEnumSchema } from "@domain/schemas/BI";
import { Chip } from "@progress/kendo-react-buttons";

interface StatusCellProps {
  status: string;
  isPauseRequested?: boolean;
  progress: number;
  isbatch: boolean;
  currentStep?: string;
  failedBatchesCount?: number;
  tdProps?: React.TdHTMLAttributes<HTMLTableCellElement>;
  selectionChange?: (event: { syntheticEvent: React.SyntheticEvent<any> }) => void;
}

const StatusCell: React.FC<StatusCellProps> = ({ status, isPauseRequested = false, progress, isbatch, currentStep, failedBatchesCount, tdProps, selectionChange }) => {
  const getChipThemeColor = () => {
    switch (status) {
      case StatusEnumSchema.Failed:
      case StatusEnumSchema.Aborted:
      case StatusEnumSchema.Paused:
        return "error";
      case StatusEnumSchema.Running:
        return "info";
      case StatusEnumSchema.Completed:
        return "success";
      case StatusEnumSchema.Created:
        return "base";
      case StatusEnumSchema.Pending:
        return "warning";
      default:
        return "base";
    }
  };

  const showProgress = status === StatusEnumSchema.Running || (status === StatusEnumSchema.Pending && !isbatch);

  return (
    <td
      {...tdProps}
      style={{ ...(tdProps?.style ?? {}), padding: "4px 8px" }}
      onClick={(event) => {
        if (tdProps?.onClick) {
          tdProps.onClick(event);
        } else {
          selectionChange?.({ syntheticEvent: event });
        }
      }}
    >
      {/* Row 1: Status Chip, Failures, and Step Name */}
      <div style={{ display: "flex", alignItems: "center", gap: "6px", flexWrap: "nowrap", minWidth: "160px", marginTop: "5px", pointerEvents: "none" }}>
        <Chip
          style={{ pointerEvents: "none", flexShrink: 0 }}
          themeColor={getChipThemeColor()}
          selected={false}
        >
          {status}
        </Chip>

        {isPauseRequested && !isbatch && (
          <Chip
            size="small"
            themeColor="warning"
            fillMode="outline"
            style={{ pointerEvents: "none", flexShrink: 0, borderRadius: "6px" }}
          >
            Pause Requested
          </Chip>
        )}

        {/* Use > 0 to ensure the number 0 never renders as text */}
        {typeof failedBatchesCount === "number" && failedBatchesCount > 0 && status === StatusEnumSchema.Completed && (
          <span title={`${failedBatchesCount} batches failed`}>
            <Chip
              size="small"
              themeColor="warning"
              fillMode="outline"
              style={{ pointerEvents: "none", flexShrink: 0, borderRadius: "6px" }}
            >
              {failedBatchesCount} Failed
            </Chip>
          </span>
        )}

        <span style={{
          fontSize: "12px",
          color: "#666",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          paddingLeft: "5px",
          paddingTop: "5px",
          flexShrink: 1,
          visibility: currentStep && showProgress ? "visible" : "hidden"
        }}>
          {currentStep ? currentStep : ""}
        </span>
      </div>

      {/* Row 2: Progress bar and percentage */}
      <div
        className="custom-progress-bar"
        style={{
          display: "flex",
          alignItems: "center",
          gap: "1px",
          width: "100%",
          marginTop: "2px",
          visibility: showProgress ? "visible" : "hidden",
          pointerEvents: "none"
        }}
      >
        <ProgressBar value={progress} style={{ height: "5px", flex: 1 }} />
        <span style={{ fontSize: "12px", color: "#666", minWidth: "35px", textAlign: "right", paddingRight: "5px" }}>
          {Math.round(progress)}%
        </span>
      </div>
    </td>
  );
};

export default StatusCell;
